import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { GlobalNavigation } from 'site-modules/shared/components/global-navigation-v2/global-navigation';
import { Footer } from 'site-modules/shared/components/footer/footer';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { SpaLinks } from 'site-modules/shared/components/spa-links/spa-links';
import { ExperimentsPanel } from 'site-modules/shared/components/experiment/panel/experiments-panel';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { ErrorPageMessage } from 'site-modules/shared/components/error-page-message/error-page-message';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { VisitorModel } from 'client/data/models/visitor';
import { SEOHead } from 'site-modules/shared/pages/seo';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { getTopRatedThemeContentPath } from 'client/site-modules/shared/utils/top-rated/content-path';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getThemeToUse, getYearToUse } from 'site-modules/shared/utils/top-rated/use-theme';
import { SkipToMainContent } from 'site-modules/shared/components/skip-to-main-content/skip-to-main-content';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';
import { AdsContextSwitch } from 'site-modules/shared/components/ads-context/ads-context-switch';
import { StickyBanner } from 'site-modules/shared/components/sticky-banner/sticky-banner';
import { ProfileContextAsync } from 'site-modules/shared/components/profile/profile-context-async';
import { getSectValues } from 'site-modules/shared/utils/top-rated/get-sect-values';

function EdmundsTopRatedDecoratorUI({
  themeContent = null,
  isMobile = false,
  isDefaultYear = false,
  withAiLink = true,
  isTopRatedLatestThemeEnabled = false,
  ...restProps
}) {
  const props = {
    themeContent,
    isMobile,
    isDefaultYear,
    withAiLink,
    isTopRatedLatestThemeEnabled,
    ...restProps,
  };
  const { type, year } = props.match.params;
  const themeToUse = getThemeToUse(themeContent);
  const yearToUse = getYearToUse(themeContent, year);
  const hasInlineAd = !!getSectValues(yearToUse, type);
  const useGlassTheme = yearToUse === '2026';
  const use2027Theme = yearToUse === '2027';

  return (
    <Fragment>
      <div
        className={classnames(
          'edm-page has-global-nav page-gradient',
          `top-rated-${themeToUse}`,
          topRatedHelpers.getGradientClass(type),
          {
            desktop: !isMobile,
          }
        )}
      >
        <ErrorBoundary>
          <SkipToMainContent />
          <GlobalNavigation
            showSearchBar
            useGlassTheme={useGlassTheme}
            use2027Theme={use2027Theme}
            withAiLink={withAiLink}
          />
        </ErrorBoundary>
        <ErrorBoundary>
          <SEOHead {...props} />
        </ErrorBoundary>
        <ErrorBoundary>
          <FeatureFlag name="testSpa">
            <SpaLinks />
          </FeatureFlag>
        </ErrorBoundary>
        <ErrorBoundary Fallback={ErrorPageMessage}>{props.children}</ErrorBoundary>
        <ErrorBoundary>
          <Footer />
        </ErrorBoundary>
        <ErrorBoundary>
          <FeatureFlag name="insider">
            <ProfileContextAsync />
          </FeatureFlag>
        </ErrorBoundary>
        <ErrorBoundary>
          <ClientSideRender>
            <ExperimentsPanel />
          </ClientSideRender>
        </ErrorBoundary>
      </div>
      {!isDefaultYear && !hasInlineAd && (
        <ErrorBoundary>
          <FeatureFlag name="ads">
            <ErrorBoundary>
              <StickyBanner hasEtrInlineAd={hasInlineAd} />
            </ErrorBoundary>
            <ErrorBoundary>
              <AdsContextSwitch />
            </ErrorBoundary>
          </FeatureFlag>
        </ErrorBoundary>
      )}
      {hasInlineAd && (
        <ErrorBoundary>
          <FeatureFlag name="ads">
            <FeatureFlag name="enableEtrRankingsAd">
              <ErrorBoundary>
                <StickyBanner hasEtrInlineAd={hasInlineAd} />
              </ErrorBoundary>
              <ErrorBoundary>
                <AdsContextSwitch />
              </ErrorBoundary>
            </FeatureFlag>
          </FeatureFlag>
        </ErrorBoundary>
      )}
    </Fragment>
  );
}

EdmundsTopRatedDecoratorUI.propTypes = {
  children: PropTypes.node.isRequired,
  themeContent: CmsEntities.Content,
  location: PropTypes.shape({}).isRequired,
  isMobile: PropTypes.bool,
  isTopRatedLatestThemeEnabled: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      type: PropTypes.string,
      year: PropTypes.string,
    }),
  }).isRequired,
  isDefaultYear: PropTypes.bool,
  withAiLink: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  themeContent: bindToContent(({ year }) => getTopRatedThemeContentPath(year)),
};

export const EdmundsTopRatedDecorator = connect(mapStateToProps)(
  connectToModel(EdmundsTopRatedDecoratorUI, stateToPropsConfig)
);

EdmundsTopRatedDecorator.preload = store => {
  const preloader = new ModelPreloader(store);
  preloader.resolve('rttSegment', VisitorModel);

  return preloader.load();
};
